import {
  Send,
  CreditCard,
  Clock,
  AlertCircle,
  LogOut,
  Settings,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { usePayments, useUserData } from '@/hooks/useCpn'
import { capitalizeFirstName } from '@/lib/utils'
import { authService } from '@/services/auth'
import { getPaymentStatusInfo } from '@/utils/cpn'

import { PaymentAmountInCad, TotalSentInCad } from '../ui/CurrencyComponents'

interface DashboardScreenProps {
  onStartPayment: () => void
  onCheckPaymentStatus: () => void
  onViewAllPayments?: () => void
  onViewPaymentDetails?: (paymentId: string) => void
  onManageBeneficiaries: () => void
  onManagePersonalInfo: () => void
  onOpenSettings: () => void
  cadUsdcRate?: number
}

const DashboardScreen = ({
  onStartPayment,
  onCheckPaymentStatus,
  onViewAllPayments,
  onViewPaymentDetails,
  onManageBeneficiaries,
  onOpenSettings,
  cadUsdcRate,
}: DashboardScreenProps) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  const authData = authService.getAuthData()
  const isAuthenticated = authService.isAuthenticated()
  const userEmail = authData?.email
  const userName = userEmail ? userEmail.split('@')[0] : ''
  const userId = authService.getUserId()

  // Fetch user data using the new hook
  const { data: userData } = useUserData(!!userId && isAuthenticated)

  // Don't fetch payments if not authenticated
  const {
    data: paymentsResponse,
    isLoading,
    error,
  } = usePayments(userId, undefined, !!userId && isAuthenticated)
  const payments = paymentsResponse?.payments || []

  // If not authenticated, show login warning
  if (!isAuthenticated || !userEmail) {
    return (
      <div className="h-[600px] flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-6">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Authentication Required
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You need to be logged in to access the dashboard. Please sign in to
            continue.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Go to Login
          </Button>
        </div>
      </div>
    )
  }

  // Calculate stats from real payment data - include all payments regardless of status
  const totalSent = payments.reduce(
    (sum, payment) => sum + payment.fromAmount,
    0
  )
  const totalTransfers = payments.length

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true)
  }

  const handleConfirmLogout = () => {
    authService.clearToken()
    window.location.reload()
  }

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false)
  }

  // Default handlers if not provided by parent
  const handleViewAllPayments = () => {
    if (onViewAllPayments) {
      onViewAllPayments()
    } else {
      // Fallback to the existing payment status handler
      onCheckPaymentStatus()
    }
  }

  const handleViewPaymentDetails = (paymentId: string) => {
    if (onViewPaymentDetails) {
      onViewPaymentDetails(paymentId)
    } else {
      onCheckPaymentStatus()
    }
  }

  // Convert CPN payments to display format
  const recentPayments = payments.slice(0, 3).map((payment) => {
    const createdDate = new Date(payment.createdAt)
    const now = new Date()
    const diffInDays = Math.floor(
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    )

    let dateText = ''
    if (diffInDays === 0) {
      dateText = 'Today'
    } else if (diffInDays === 1) {
      dateText = 'Yesterday'
    } else if (diffInDays < 7) {
      dateText = `${diffInDays} days ago`
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7)
      dateText = `${weeks} week${weeks > 1 ? 's' : ''} ago`
    } else {
      const months = Math.floor(diffInDays / 30)
      dateText = `${months} month${months > 1 ? 's' : ''} ago`
    }

    const statusInfo = getPaymentStatusInfo(payment.status)

    return {
      id: payment.paymentUuid,
      recipient: `Transfer to ${payment.toCurrency}`, // We don't have recipient names in the API
      usdcAmount: payment.fromAmount, // Store USDC amount for conversion
      statusInfo,
      date: dateText,
      destination: payment.toCurrency,
      intentId: payment.intentId,
    }
  })

  // Helper function to check if error is "no payments data available"
  const isNoPaymentsError = (error: unknown) => {
    const errorObj = error as { response?: { data?: unknown } }
    return (
      errorObj?.response?.data === 'No payments data available' ||
      (typeof errorObj?.response?.data === 'string' &&
        errorObj.response.data.includes('No payments data available'))
    )
  }

  // Helper function to extract meaningful error message
  const getErrorMessage = (error: unknown) => {
    const errorObj = error as {
      response?: { data?: unknown }
      message?: string
    }
    if (
      errorObj?.response?.data &&
      typeof errorObj.response.data === 'string'
    ) {
      return errorObj.response.data
    }
    const dataObj = errorObj?.response?.data as { message?: string } | undefined
    if (dataObj?.message) {
      return dataObj.message
    }
    if (errorObj?.message) {
      return errorObj.message
    }
    return null
  }

  return (
    <div className="h-[600px] flex flex-col overflow-y-auto bg-white dark:bg-gray-900">
      {/* Header with User Info */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                {userData?.firstName
                  ? userData.firstName.charAt(0).toUpperCase()
                  : userEmail.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold">
                Welcome back,{' '}
                {userData?.firstName
                  ? capitalizeFirstName(userData.firstName)
                  : userName}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenSettings}
              className="text-white hover:bg-white/20"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogoutClick}
              className="text-white hover:bg-white/20"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div
            className="bg-white/10 rounded-lg p-3 cursor-pointer hover:bg-white/20 transition-colors"
            onClick={handleViewAllPayments}
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-200">Total Sent</span>
            </div>
            <p className="text-lg font-semibold">
              {isLoading ? (
                '...'
              ) : (
                <TotalSentInCad
                  usdcTotal={totalSent}
                  cadUsdcRate={cadUsdcRate}
                  fallbackToHook={!cadUsdcRate}
                />
              )}
            </p>
          </div>
          <div
            className="bg-white/10 rounded-lg p-3 cursor-pointer hover:bg-white/20 transition-colors"
            onClick={handleViewAllPayments}
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm text-blue-200">Transfers</span>
            </div>
            <p className="text-lg font-semibold">
              {isLoading ? '...' : totalTransfers}
            </p>
          </div>
        </div>
      </div>

      {/* Main Actions */}
      <div className="p-6 space-y-4 bg-white dark:bg-gray-900">
        <h2 className="text-lg font-heading font-semibold text-gray-900 dark:text-white">
          Quick Actions
        </h2>

        {/* Start Payment Button */}
        <Card
          className="p-4 cursor-pointer hover:shadow-md transition-shadow border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-600 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
          onClick={onStartPayment}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                <Send className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Send Money Worldwide
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start a new international transfer
                </p>
              </div>
            </div>
            <div className="text-blue-500 dark:text-blue-400"></div>
          </div>
        </Card>

        {/* Secondary Actions */}
        <div className="grid grid-cols-1 gap-3">
          {/* Check Payment Status */}
          <Card
            className="p-4 cursor-pointer hover:shadow-md transition-shadow border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
            onClick={onCheckPaymentStatus}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Check Payment Status
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Track your transfers
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Manage Beneficiaries */}
          <Card
            className="p-4 cursor-pointer hover:shadow-md transition-shadow border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
            onClick={onManageBeneficiaries}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Manage Recipients
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    View and edit recipients
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="px-6 pb-6 flex-1 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-gray-900 dark:text-white">
            Recent Payments
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewAllPayments}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            View All
          </Button>
        </div>

        <div className="space-y-3">
          {/* Show error state - but not for "no payments data available" */}
          {error && !isNoPaymentsError(error) && (
            <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">
                  {getErrorMessage(error) || 'Failed to load payment data'}
                </span>
              </div>
            </Card>
          )}

          {/* Show loading state */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card
                  key={i}
                  className="p-4 animate-pulse bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Show actual payments */}
          {!isLoading &&
            (!error || isNoPaymentsError(error)) &&
            recentPayments.map((payment) => (
              <Card
                key={payment.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                onClick={() => handleViewPaymentDetails(payment.intentId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                          {payment.recipient}
                        </h4>
                        <payment.statusInfo.Icon
                          className={`w-4 h-4 ${payment.statusInfo.color}`}
                        />
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {payment.destination} • {payment.date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <PaymentAmountInCad
                      usdcAmount={payment.usdcAmount}
                      cadUsdcRate={cadUsdcRate}
                      fallbackToHook={!cadUsdcRate}
                    />
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.statusInfo.color} ${payment.statusInfo.bgColor}`}
                    >
                      {payment.statusInfo.label}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
        </div>

        {!isLoading &&
          (!error || isNoPaymentsError(error)) &&
          recentPayments.length === 0 && (
            <Card className="p-8 text-center bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <Send className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                No payments yet
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Start your first international transfer today
              </p>
              <Button
                onClick={onStartPayment}
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
              >
                Send Money Now
              </Button>
            </Card>
          )}
      </div>

      {/* Sign Out Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-50">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg border-0">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Sign Out
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelLogout}
                  className="p-1"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-gray-600 text-sm dark:text-gray-300 mb-6">
                Are you sure you want to sign out?
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCancelLogout}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmLogout}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardScreen
